import type { INFTContract } from 'oscar-typedef'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import type { Maybe } from '@/types'
import { getExecuteOnce } from '@/utils'

const useNFTContractAPIStore = defineStore('nftContractAPI', () => {
  const contractList = ref<Maybe<INFTContract[]>>()
  const fetchNFTContractList = getExecuteOnce(
    async ({ ids, force = false }: { ids?: number[]; force?: boolean }) => {
      if (!ids) return
      if (force || !contractList.value) {
        const res =
          await WepinBackendAPI.getInstance().nftcontract.getNFTContractList({
            ids: ids,
          })
        if (!res) return

        // V2: getNFTContractList가 빈 배열을 반환하는 경우 처리
        // NFT 응답에 이미 nftContract가 포함되어 있으므로 별도 처리 불필요
        if (!contractList.value) contractList.value = []

        // res.nftContracts가 있는 경우에만 처리 (V1 호환성)
        if (res.nftContracts && Array.isArray(res.nftContracts)) {
          contractList.value.push(...res.nftContracts)
          contractList.value = contractList.value.filter(
            (arrItem, index, callback) =>
              index === callback.findIndex((t) => t['id'] === arrItem['id']),
          )
        }
      }
      return contractList.value
    },
  )

  const fetchNFTContract = getExecuteOnce(
    async (nftContractId: string, force: boolean | undefined = false) => {
      if (!nftContractId) return
      if (force) {
        const res =
          await WepinBackendAPI.getInstance().nftcontract.getNFTContract({
            nftContractId,
          })
        if (!res) return
        contractList.value = contractList.value?.map((contract) =>
          contract.id === res.nftContract.id ? res.nftContract : contract,
        )
        return res
      }
      return contractList.value?.find(
        (contract) => contract.id.toString() === nftContractId,
      )
    },
  )

  const getNFTContract = (nftContractId: number) => {
    return contractList.value?.find((contract) => contract.id === nftContractId)
  }

  const reset = () => {
    contractList.value = []
  }

  return {
    contractList,
    fetchNFTContractList,
    fetchNFTContract,
    getNFTContract,
    reset,
  }
})

export { useNFTContractAPIStore }
